import React, { useRef, useState } from "react";
import { format, isValid, parse } from "date-fns";
import Calendar from "../Calendar/Calendar";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import { Input } from "../Modals/DocumentModal/DocumentModal.styles";
import TitleTooltip from "../TitleTooltip/TitleTooltip";
import { CalendarInputRoot, CalendarPopup } from "./CalendarInput.styles";

interface DatePickerInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  title?: string;
}

export const parseCalendarInputDate = (value: string): Date | undefined => {
  const normalizedValue = value.trim();
  if (!normalizedValue) return undefined;

  const parsedDate = parse(normalizedValue, "dd.MM.yyyy", new Date());
  return isValid(parsedDate) ? parsedDate : undefined;
};

const DatePickerInput: React.FC<DatePickerInputProps> = ({
  value,
  onChange,
  placeholder,
  title,
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(containerRef, () => setIsCalendarOpen(false));

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start] = dates;
    const formattedDate = start ? format(start, "dd.MM.yyyy") : "";
    onChange(formattedDate);
    setIsCalendarOpen(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const handleInputBlur = () => {
    const parsedDate = parseCalendarInputDate(value);
    if (parsedDate) {
      onChange(format(parsedDate, "dd.MM.yyyy"));
    }
  };

  const selectedDate = parseCalendarInputDate(value);

  return (
    <CalendarInputRoot ref={containerRef}>
      <TitleTooltip text={title || ""}>
        <Input
          value={value}
          placeholder={placeholder}
          onChange={handleInputChange}
          onFocus={() => setIsCalendarOpen(true)}
          onClick={() => setIsCalendarOpen(true)}
          onBlur={handleInputBlur}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleInputBlur();
              setIsCalendarOpen(false);
            }
          }}
        />
      </TitleTooltip>
      {isCalendarOpen && (
        <CalendarPopup data-testid="calendar-input-popup">
          <Calendar
            onDateChange={handleDateChange}
            startDate={selectedDate}
            isRange={false}
          />
        </CalendarPopup>
      )}
    </CalendarInputRoot>
  );
};

export default DatePickerInput;
